import React, { useMemo, useState } from "react";
import {
  Card,
  Button,
  ProgressBar,
  Badge,
  Alert,
  ButtonGroup,
  ListGroup,
} from "react-bootstrap";
import {
  CheckCircleFill,
  XCircleFill,
  InfoCircle,
  ShieldCheck,
  ArrowRight,
  ArrowLeft,
  ArrowRepeat,
  Share as ShareIcon,
} from "react-bootstrap-icons";

/**
 * Myth vs Fact – Interactive Quiz Wizard (Car Insurance)
 *
 * Tech: React + TypeScript + react-bootstrap + react-bootstrap-icons
 * UX: Welcome → Questions (one per step) → Results + Review
 *
 * Features
 * - Keyboard accessible (Myth=M, Fact=F, Next=Enter)
 * - Progress bar and step navigation
 * - Instant feedback after each choice (now includes 3 examples per item)
 * - Review screen with explanations, examples, and per-question correctness
 * - Restart & Web Share API
 * - Data-driven: pass your own items or use DEFAULT_ITEMS
 */

export type Verdict = "Myth" | "Fact";

export interface QuizItem {
  id: string;
  /** A short statement for the user to judge as Myth or Fact */
  statement: string;
  /** The correct verdict for the statement */
  correct: Verdict;
  /** Explanation shown after answering */
  explanation: string;
  /** Three concrete examples to reinforce the concept */
  examples: [string, string, string];
  /** Optional tag shown as a Badge (e.g., Coverage, Claims, Discounts) */
  tag?: string;
}

export interface QuizWizardProps {
  items?: QuizItem[];
  title?: string;
  subtitle?: string;
  /** Called when the user completes all questions */
  onComplete?: (score: number, total: number) => void;
}

const DEFAULT_ITEMS: QuizItem[] = [
  {
    id: "q1",
    statement:
      "If my car is older, I can drop liability coverage to save money.",
    correct: "Myth",
    explanation:
      "Liability coverage is required by law in most states regardless of vehicle age. You might drop collision or comprehensive on very old cars, but liability protects you from paying for injuries or property damage you cause.",
    examples: [
      "You rear-end someone at a stoplight in a 2005 sedan—liability pays their bumper, not yours.",
      "Your teen clips a mailbox—liability covers the mailbox owner's damage.",
      "A fender-bender causes whiplash to another driver—bodily injury liability responds up to your limits.",
    ],
    tag: "Liability",
  },
  {
    id: "q2",
    statement:
      "Red cars cost more to insure than other colors because they get more tickets.",
    correct: "Myth",
    explanation:
      "Insurers generally don’t ask for vehicle color. Rates are based on factors like make/model, age, usage, driving record, location, and coverage limits—not paint color.",
    examples: [
      "A red Honda Civic and a blue Honda Civic with identical drivers rate the same all else equal.",
      "A gray sports car may cost more than a red minivan due to performance/claim data, not color.",
      "Changing your wrap from white to matte black does not change premium with most carriers.",
    ],
    tag: "Pricing",
  },
  {
    id: "q3",
    statement:
      "Comprehensive coverage can help if my windshield is cracked by a rock.",
    correct: "Fact",
    explanation:
      "Comprehensive typically covers non-collision damage like glass, theft, fire, hail, and animal strikes (subject to your deductible and policy terms).",
    examples: [
      "A rock chips your windshield on the highway—glass claim under comprehensive.",
      "Hail dents your hood during a storm—comprehensive handles weather damage.",
      "A deer runs into your car—animal strike is comprehensive, not collision, with most policies.",
    ],
    tag: "Comprehensive",
  },
  {
    id: "q4",
    statement:
      "If a friend borrows my car and crashes, their insurance is primary, not mine.",
    correct: "Myth",
    explanation:
      "In many states, insurance follows the car first. Your policy may be primary when your vehicle is involved, with the driver’s policy potentially excess. Always check your policy and state rules.",
    examples: [
      "You lend your SUV to a neighbor—your policy likely responds first in an at-fault crash.",
      "Your friend’s policy may contribute if damages exceed your limits (excess).",
      "If the driver is excluded on your policy, coverage could be denied—know your endorsements.",
    ],
    tag: "Claims",
  },
  {
    id: "q5",
    statement:
      "Minimum state liability limits are often not enough to protect my assets.",
    correct: "Fact",
    explanation:
      "State minimums can be very low. Serious accidents can exceed those limits quickly, leaving you personally responsible for the difference. Higher limits or an umbrella policy can help.",
    examples: [
      "A multi-car pileup with injuries can easily exceed a 25/50/25 minimum limit.",
      "A new luxury SUV you total could surpass a $25k property damage limit.",
      "An umbrella policy adds $1M+ liability above your auto/home limits.",
    ],
    tag: "Limits",
  },
  {
    id: "q6",
    statement:
      "Personal auto insurance covers me while driving for rideshare apps (Uber/Lyft).",
    correct: "Myth",
    explanation:
      "Most personal policies exclude commercial/rideshare use unless you add a rideshare endorsement. Without proper coverage, you may have gaps when the app is on.",
    examples: [
      "You’re waiting for a ride request (Period 1)—a rideshare endorsement often needed.",
      "En route to pick up a rider (Period 2)—personal policy may exclude; TNC coverage varies.",
      "Passenger in car (Period 3)—TNC liability may apply; check deductibles and comp/collision.",
    ],
    tag: "Rideshare",
  },
  {
    id: "q7",
    statement:
      "Uninsured/Underinsured Motorist (UM/UIM) can pay my medical bills if the other driver has little or no insurance.",
    correct: "Fact",
    explanation:
      "UM/UIM is designed to help when an at-fault driver lacks adequate coverage, up to your policy’s UM/UIM limits.",
    examples: [
      "Hit-and-run with injuries—UM can respond when the other driver can’t be identified.",
      "Other driver carries low limits—UIM helps cover remaining medical costs.",
      "Pedestrian struck by a driver with no insurance—UM may apply, per state/policy.",
    ],
    tag: "UM/UIM",
  },
  {
    id: "q8",
    statement:
      "My credit (in states where allowed) can affect my auto insurance rate.",
    correct: "Fact",
    explanation:
      "In many states, insurers use credit-based insurance scores as a risk factor. Some states restrict or prohibit this—check local rules.",
    examples: [
      "Two identical drivers in the same city—one with higher credit may see lower premiums.",
      "State bans on credit use (e.g., certain states) remove this factor entirely.",
      "Improving on-time payments over time may help premiums where allowed.",
    ],
    tag: "Pricing",
  },
  {
    id: "q9",
    statement:
      "After a not-at-fault accident, my rates can never go up.",
    correct: "Myth",
    explanation:
      "Not-at-fault claims can still influence premiums depending on the insurer, claim type, and state rules. Frequency and severity of claims also matter.",
    examples: [
      "Multiple not-at-fault glass claims could impact renewal pricing with some carriers.",
      "Comprehensive theft claim may affect tiering even if you weren’t at fault.",
      "Surcharges vary—some states/insurers prohibit surcharges for certain not-at-fault claims.",
    ],
    tag: "Pricing",
  },
  {
    id: "q10",
    statement:
      "Gap insurance helps if my financed or leased car is totaled but I owe more than its value.",
    correct: "Fact",
    explanation:
      "Gap (Guaranteed Asset Protection) can cover the difference between the vehicle’s actual cash value and the remaining loan/lease balance, per policy terms.",
    examples: [
      "Car worth $20k but loan balance is $25k after a total loss—Gap can pay the $5k difference.",
      "Leased vehicles often require Gap coverage in the contract.",
      "High-interest loans with low down payments are more prone to needing Gap.",
    ],
    tag: "Gap",
  },
];

const IconForVerdict: React.FC<{ verdict: Verdict }> = ({ verdict }) =>
  verdict === "Fact" ? (
    <CheckCircleFill className="ms-1" aria-label="Fact" />
  ) : (
    <XCircleFill className="ms-1" aria-label="Myth" />
  );

const percent = (num: number, den: number) => (den === 0 ? 0 : Math.round((num / den) * 100));

const friendlyScore = (scorePct: number) => {
  if (scorePct >= 90) return "Insurance Ace";
  if (scorePct >= 75) return "Coverage Pro";
  if (scorePct >= 60) return "Getting There";
  return "Rookie – Great Start";
};

export const MythVsFactCarInsuranceQuizWizard_v01: React.FC<QuizWizardProps> = ({
  items = DEFAULT_ITEMS,
  title = "Car Insurance: Myth vs Fact",
  subtitle = "Test what you know in 10 quick steps.",
  onComplete,
}) => {
  const total = items.length;
  const [started, setStarted] = useState(false);
  const [current, setCurrent] = useState(0);
  const [answers, setAnswers] = useState<Record<string, Verdict | undefined>>({});
  const [showFeedback, setShowFeedback] = useState(false);

  const score = useMemo(
    () =>
      items.reduce((acc, q) => (answers[q.id] === q.correct ? acc + 1 : acc), 0),
    [answers, items]
  );

  const completed = current >= total;
  const progress = completed ? 100 : percent(current, total);

  const handleChoice = (verdict: Verdict) => {
    const id = items[current].id;
    setAnswers((prev) => ({ ...prev, [id]: verdict }));
    setShowFeedback(true);
  };

  const goNext = () => {
    const next = current + 1;
    if (next === total) {
      // move to results page
      setCurrent(next);
      onComplete?.(score, total);
    } else {
      setCurrent(next);
    }
    setShowFeedback(false);
  };

  const goPrev = () => {
    if (current > 0) {
      setCurrent((c) => c - 1);
      setShowFeedback(false);
    }
  };

  const restart = () => {
    setStarted(false);
    setCurrent(0);
    setAnswers({});
    setShowFeedback(false);
  };

  const onKeyDown: React.KeyboardEventHandler<HTMLDivElement> = (e) => {
    if (!started || completed) return;
    if (e.key.toLowerCase() === "m") handleChoice("Myth");
    if (e.key.toLowerCase() === "f") handleChoice("Fact");
    if (e.key === "Enter" && showFeedback) goNext();
  };

  const shareResults = async () => {
    const scorePct = percent(score, total);
    const text = `I scored ${score}/${total} (${scorePct}%) on a Car Insurance Myth vs Fact quiz!`;
    // Try Web Share API
    if (navigator.share) {
      try {
        await navigator.share({ title: "Myth vs Fact Quiz", text });
      } catch (_) {
        // ignore
      }
    } else {
      await navigator.clipboard.writeText(text);
      alert("Results copied to clipboard!");
    }
  };

  const Header: React.FC = () => (
    <div className="d-flex align-items-center justify-content-between mb-3">
      <div>
        <h3 className="mb-0 d-flex align-items-center">
          <ShieldCheck className="me-2" /> {title}
        </h3>
        {subtitle && <small className="text-muted">{subtitle}</small>}
      </div>
      <div style={{ minWidth: 160 }}>
        <ProgressBar now={progress} label={`${progress}%`} visuallyHidden={false} />
      </div>
    </div>
  );

  if (!started) {
    return (
      <Card role="region" aria-label="Quiz intro">
        <Card.Body className="p-4">
          <Header />
          <p className="mb-3">
            Decide whether each statement is a <strong>Myth</strong> or a <strong>Fact</strong>. Press <kbd>M</kbd> for Myth, <kbd>F</kbd> for Fact. After you answer, you'll see a short explanation and <strong>three real-world examples</strong>.
          </p>
          <ul className="mb-4">
            <li>10 quick questions</li>
            <li>Instant feedback with explanations + 3 examples</li>
            <li>Results summary and review</li>
          </ul>
          <div className="d-flex gap-2">
            <Button size="lg" onClick={() => setStarted(true)}>
              Start Quiz <ArrowRight className="ms-1" />
            </Button>
          </div>
        </Card.Body>
      </Card>
    );
  }

  if (completed) {
    const scorePct = percent(score, total);
    const label = friendlyScore(scorePct);

    return (
      <Card role="region" aria-label="Quiz results">
        <Card.Body className="p-4">
          <Header />
          <div className="mb-3">
            <h4 className="mb-1">Results</h4>
            <p className="mb-2">
              You answered <strong>{score}</strong> of <strong>{total}</strong> correctly (<strong>{scorePct}%</strong>). <Badge bg={scorePct >= 75 ? "success" : scorePct >= 60 ? "warning" : "secondary"}>{label}</Badge>
            </p>
            <div className="d-flex gap-2">
              <Button variant="primary" onClick={restart}>
                <ArrowRepeat className="me-1" /> Restart
              </Button>
              <Button variant="outline-secondary" onClick={shareResults}>
                <ShareIcon className="me-1" /> Share
              </Button>
            </div>
          </div>

          <hr />

          <h5 className="mb-3">Review Answers</h5>
          {items.map((q, idx) => {
            const user = answers[q.id];
            const correct = user === q.correct;
            return (
              <Alert
                key={q.id}
                variant={correct ? "success" : "danger"}
                className="mb-3"
                aria-live="polite"
              >
                <div className="d-flex align-items-start justify-content-between">
                  <div>
                    <div className="mb-1">
                      <Badge bg="info" className="me-2">#{idx + 1}</Badge>
                      {q.tag && (
                        <Badge bg="secondary" className="me-2">{q.tag}</Badge>
                      )}
                      <span className="fw-semibold">{q.statement}</span>
                    </div>
                    <div className="mb-1">
                      Your answer: <strong>{user ?? "—"}</strong> {typeof user !== "undefined" && <IconForVerdict verdict={user} />}
                      <span className="ms-2">•</span>
                      <span className={correct ? "text-success ms-2" : "text-danger ms-2"}>
                        {correct ? "Correct" : "Incorrect"}
                      </span>
                    </div>
                    <div className="small text-muted d-flex align-items-center mb-2">
                      <InfoCircle className="me-1" /> {q.explanation}
                    </div>
                    <ListGroup as="ol" numbered>
                      {q.examples.map((ex, i) => (
                        <ListGroup.Item as="li" key={i} className="small">
                          {ex}
                        </ListGroup.Item>
                      ))}
                    </ListGroup>
                  </div>
                </div>
              </Alert>
            );
          })}
        </Card.Body>
      </Card>
    );
  }

  const q = items[current];
  const userAnswer = answers[q.id];
  const isCorrect = userAnswer && userAnswer === q.correct;

  return (
    <Card role="region" aria-label={`Question ${current + 1} of ${total}`} onKeyDown={onKeyDown} tabIndex={0}>
      <Card.Body className="p-4">
        <Header />
        <div className="d-flex align-items-center mb-2">
          <Badge bg="info" className="me-2">{current + 1} / {total}</Badge>
          {q.tag && <Badge bg="secondary">{q.tag}</Badge>}
        </div>

        <h5 className="mb-3">{q.statement}</h5>

        <ButtonGroup className="mb-3" aria-label="Choose Myth or Fact">
          <Button
            variant={userAnswer === "Myth" ? "primary" : "outline-primary"}
            onClick={() => handleChoice("Myth")}
            aria-pressed={userAnswer === "Myth"}
          >
            Myth <XCircleFill className="ms-1" />
          </Button>
          <Button
            variant={userAnswer === "Fact" ? "success" : "outline-success"}
            onClick={() => handleChoice("Fact")}
            aria-pressed={userAnswer === "Fact"}
          >
            Fact <CheckCircleFill className="ms-1" />
          </Button>
        </ButtonGroup>

        {showFeedback && typeof userAnswer !== "undefined" && (
          <Alert variant={isCorrect ? "success" : "danger"} aria-live="polite">
            <div className="d-flex align-items-start">
              <div className="me-2">
                {isCorrect ? (
                  <CheckCircleFill aria-hidden />
                ) : (
                  <XCircleFill aria-hidden />
                )}
              </div>
              <div>
                <div className="fw-semibold mb-1">{isCorrect ? "Correct" : "Not quite"}</div>
                <div className="small text-muted mb-2"><InfoCircle className="me-1" /> {q.explanation}</div>
                <div className="fw-semibold small mb-1">Examples</div>
                <ListGroup as="ol" numbered>
                  {q.examples.map((ex, i) => (
                    <ListGroup.Item as="li" key={i} className="small">
                      {ex}
                    </ListGroup.Item>
                  ))}
                </ListGroup>
              </div>
            </div>
          </Alert>
        )}

        <div className="d-flex justify-content-between mt-3">
          <Button
            variant="outline-secondary"
            onClick={goPrev}
            disabled={current === 0}
          >
            <ArrowLeft className="me-1" /> Back
          </Button>
          <div className="d-flex align-items-center gap-2">
            <span className="text-muted small d-none d-md-inline">
              Press <kbd>M</kbd> for Myth, <kbd>F</kbd> for Fact
            </span>
            <Button
              variant="primary"
              onClick={goNext}
              disabled={typeof userAnswer === "undefined"}
            >
              {current + 1 === total ? "Finish" : "Next"} <ArrowRight className="ms-1" />
            </Button>
          </div>
        </div>
      </Card.Body>
    </Card>
  );
};

export default MythVsFactCarInsuranceQuizWizard_v01;

// Quick mount helper (optional):
// import ReactDOM from "react-dom/client";
// const root = ReactDOM.createRoot(document.getElementById("root")!);
// root.render(<MythVsFactCarInsuranceQuizWizard />);
